"use client";

import { useState, useRef, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  House, Compass, CalendarDays, LayoutDashboard, MessageCircle,
  LogIn, LogOut, Upload, Settings, Ellipsis, User, Scissors,
  BookOpen, Activity, ShoppingBag, Wifi, Camera, Sparkles, Cloud, Pencil, Award, GraduationCap, Download, Video, Radio
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useScrollVisibility } from "@/hooks/useScrollVisibility";
import { useChatStore } from "@/lib/chat-store";
import { useOverlayStore } from "@/lib/overlay-store";
import { useAuth } from "@/components/AuthProvider";
import { useTenant, type BusinessType } from "@/components/TenantProvider";

type DockItem = { href: string; label: string; icon: typeof House };

/* ─── PHOTOGRAPHY (Adara) — Gallery, Booking, Dashboard ─── */
const PHOTOGRAPHY_UNAUTH: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/explore", label: "Portofolio", icon: Camera },
];
const PHOTOGRAPHY_CLIENT: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/explore", label: "Galeri", icon: Camera },
  { href: "/booking", label: "Booking", icon: CalendarDays },
];
const PHOTOGRAPHY_CLIENT_MORE: DockItem[] = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/profile", label: "Profil", icon: User },
];
const PHOTOGRAPHY_ADMIN: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/admin/bookings", label: "Pesanan", icon: CalendarDays },
  { href: "/admin/portfolio", label: "Portfolio", icon: Upload },
  { href: "/admin/services", label: "Services", icon: ShoppingBag },
];
const PHOTOGRAPHY_ADMIN_MORE: DockItem[] = [
  { href: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/homepage", label: "Homepage", icon: Pencil },
  { href: "/admin/users", label: "Users", icon: Settings },
];

/* ─── SALON (VA Salon) — Services, Booking, Reviews ─── */
const SALON_UNAUTH: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/explore", label: "Layanan", icon: Sparkles },
];
const SALON_CLIENT: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/explore", label: "Layanan", icon: Scissors },
  { href: "/booking", label: "Booking", icon: CalendarDays },
];
const SALON_CLIENT_MORE: DockItem[] = [
  { href: "/dashboard", label: "Janji Saya", icon: LayoutDashboard },
  { href: "/profile", label: "Profil", icon: User },
];
const SALON_ADMIN: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/admin/salon-bookings", label: "Janji", icon: CalendarDays },
  { href: "/admin/salon-services", label: "Layanan", icon: Scissors },
];
const SALON_ADMIN_MORE: DockItem[] = [
  { href: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/users", label: "Klien", icon: Settings },
];

/* ─── PERSONAL (Ipung) — Bersih, 3-4 ikon, MikroTik order ─── */
const PERSONAL_UNAUTH: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/blog", label: "Blog", icon: BookOpen },
  { href: "/learn", label: "Modul", icon: GraduationCap },
];
const PERSONAL_CLIENT: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/learn", label: "Modul", icon: GraduationCap },
  { href: "/dashboard", label: "Progres", icon: LayoutDashboard },
];
const PERSONAL_CLIENT_MORE: DockItem[] = [
  { href: "/blog", label: "Blog", icon: BookOpen },
  { href: "/tools", label: "Tools", icon: Download },
  { href: "/content", label: "Content", icon: Radio },
  { href: "/live", label: "Live", icon: Video },
  { href: "/profile", label: "Profil", icon: User },
];
const PERSONAL_ADMIN: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/admin/lms", label: "LMS", icon: Activity },
  { href: "/admin/blog", label: "Blog", icon: Pencil },
];
const PERSONAL_ADMIN_MORE: DockItem[] = [
  { href: "/admin/modules", label: "Modul", icon: GraduationCap },
  { href: "/admin/certifications", label: "Sertifikasi", icon: Award },
  { href: "/admin/users", label: "Users", icon: Settings },
];

/* ─── IoT (Karpung) — Dashboard, Monitoring, Products ─── */
const IOT_UNAUTH: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/explore", label: "Produk", icon: Wifi },
];
const IOT_CLIENT: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/explore", label: "Produk", icon: ShoppingBag },
  { href: "/booking", label: "Order", icon: CalendarDays },
];
const IOT_CLIENT_MORE: DockItem[] = [
  { href: "/dashboard", label: "Monitoring", icon: Activity },
  { href: "/profile", label: "Profil", icon: User },
];
const IOT_ADMIN: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/admin/dashboard", label: "Monitoring", icon: Activity },
];
const IOT_ADMIN_MORE: DockItem[] = [
  { href: "/admin/bookings", label: "Orders", icon: CalendarDays },
  { href: "/admin/users", label: "Klien", icon: Settings },
];

/* ─── EDUCATION (SMKN3) ─── */
const EDUCATION_ADMIN: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin", label: "Sekolah", icon: GraduationCap },
];

/* ─── DEFAULT (generic) ─── */
const DEFAULT_UNAUTH: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/explore", label: "Jelajah", icon: Compass },
];
const DEFAULT_CLIENT: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/explore", label: "Jelajah", icon: Compass },
  { href: "/booking", label: "Booking", icon: CalendarDays },
];
const DEFAULT_CLIENT_MORE: DockItem[] = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/profile", label: "Profil", icon: User },
];
const DEFAULT_ADMIN: DockItem[] = [
  { href: "/", label: "Home", icon: House },
  { href: "/admin/bookings", label: "Pesanan", icon: CalendarDays },
];
const DEFAULT_ADMIN_MORE: DockItem[] = [
  { href: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/users", label: "Users", icon: Settings },
];

/* ─── Registry ─── */
function getDockItems(bt: BusinessType, role: "unauth" | "client" | "admin" | "clientMore" | "adminMore"): DockItem[] {
  const map: Record<string, Record<string, DockItem[]>> = {
    photography: {
      unauth: PHOTOGRAPHY_UNAUTH, client: PHOTOGRAPHY_CLIENT,
      clientMore: PHOTOGRAPHY_CLIENT_MORE, admin: PHOTOGRAPHY_ADMIN,
      adminMore: PHOTOGRAPHY_ADMIN_MORE,
    },
    salon: {
      unauth: SALON_UNAUTH, client: SALON_CLIENT,
      clientMore: SALON_CLIENT_MORE, admin: SALON_ADMIN,
      adminMore: SALON_ADMIN_MORE,
    },
    personal: {
      unauth: PERSONAL_UNAUTH, client: PERSONAL_CLIENT,
      clientMore: PERSONAL_CLIENT_MORE, admin: PERSONAL_ADMIN,
      adminMore: PERSONAL_ADMIN_MORE,
    },
    iot_agriculture: {
      unauth: IOT_UNAUTH, client: IOT_CLIENT,
      clientMore: IOT_CLIENT_MORE, admin: IOT_ADMIN,
      adminMore: IOT_ADMIN_MORE,
    },
    education: {
      unauth: DEFAULT_UNAUTH, client: DEFAULT_CLIENT,
      clientMore: DEFAULT_CLIENT_MORE, admin: EDUCATION_ADMIN,
      adminMore: [],
    },
    default: {
      unauth: DEFAULT_UNAUTH, client: DEFAULT_CLIENT,
      clientMore: DEFAULT_CLIENT_MORE, admin: DEFAULT_ADMIN,
      adminMore: DEFAULT_ADMIN_MORE,
    },
  };
  return map[bt]?.[role] || map.default[role];
}

/* ================================================================ */
export default function FloatingDock() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated, logout } = useAuth();
  const { tenant } = useTenant();
  const { visible } = useScrollVisibility({ threshold: 15, showDelay: 600 });
  const { openChat, getUnreadTotal } = useChatStore();
  const unreadTotal = getUnreadTotal();
  const overlayActive = useOverlayStore((s) => s.activeCount > 0);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleLogout = () => { setMoreOpen(false); logout(); navigate("/"); };

  const bt = tenant.businessType;
  const isPersonal = bt === "personal" || bt === "education";
  if (isPersonal) return null; // Ipung uses top navbar, not dock
  const accentColor = "var(--color-gold)";
  const items = isAdmin
    ? getDockItems(bt, "admin")
    : isAuthenticated
      ? getDockItems(bt, "client")
      : getDockItems(bt, "unauth");
  const moreItems = !isAuthenticated ? [] : isAdmin
    ? getDockItems(bt, "adminMore")
    : getDockItems(bt, "clientMore");
  const showMore = isAuthenticated;
  const hasChat = tenant.settings.features?.chat !== false;

  const DockIcon = ({ active, children }: { active?: boolean; children: React.ReactNode }) => (
    <motion.div
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.92 }}
      className={cn(
        "flex items-center justify-center transition-colors duration-200",
        isPersonal
          ? cn("rounded-md", active ? "text-[#0054A6]" : "text-gray-400", "hover:text-[#0054A6]")
          : cn("rounded-2xl", active ? "text-gold" : "text-dark-text-secondary", "group-hover:text-gold hover:text-gold"),
      )}
    >
      {children}
    </motion.div>
  );

  return (
    <motion.nav
      initial={{ y: 120, opacity: 0 }}
      animate={{ y: visible && !overlayActive ? 0 : 120, opacity: visible && !overlayActive ? 1 : 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 24, mass: 0.8 }}
      className={cn(
        "fixed bottom-6 left-1/2 -translate-x-1/2 z-50",
        "flex items-center gap-0.5",
        "px-3 py-2.5",
        isPersonal
          ? cn(
              "rounded-md",
              "bg-white dark:bg-[#0e0e10]",
              "border border-[#dadada] dark:border-gray-700",
              "shadow-sm",
            )
          : cn(
              "rounded-[20px]",
              "bg-dark-card/80 dark:bg-dark-card/80 bg-white/80",
              "backdrop-blur-xl",
              "border border-dark-text-secondary/10 dark:border-white/10",
              "shadow-2xl shadow-black/25 dark:shadow-black/40",
            ),
      )}
    >
      {/* ── Nav items ── */}
      {items.map((item) => {
        const isActive = item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
        return (
          <Link
            key={item.href}
            to={item.href}
            className={cn(
              "relative flex items-center justify-center w-11 h-11 transition-all duration-200 group",
              isPersonal ? "rounded-md" : "rounded-2xl",
            )}
          >
            <DockIcon active={isActive}>
              <item.icon className="w-5 h-5" strokeWidth={isPersonal ? 1 : undefined} />
            </DockIcon>
            {isActive && (
              <motion.div
                layoutId="dock-indicator"
                transition={{ type: "spring" as const, stiffness: 500, damping: 32 }}
                className="absolute -bottom-1 w-1 h-1 rounded-full"
                style={{ backgroundColor: accentColor }}
              />
            )}
          </Link>
        );
      })}

      {/* ── Chat (authenticated + tenant feature enabled) ── */}
      {isAuthenticated && hasChat && (
        <button
          onClick={openChat}
          className={cn("relative flex items-center justify-center w-11 h-11 transition-all duration-200 group", isPersonal ? "rounded-md" : "rounded-2xl")}
          aria-label="Pesan"
        >
          <DockIcon>
            <MessageCircle className="w-5 h-5" />
          </DockIcon>
          {unreadTotal > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-sm">
              {unreadTotal > 9 ? "9+" : unreadTotal}
            </span>
          )}
        </button>
      )}

      {/* ── More ⋯ ── */}
      {showMore && (
        <div className="relative" ref={moreRef}>
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={cn("relative flex items-center justify-center w-11 h-11 transition-all duration-200 group", isPersonal ? "rounded-md" : "rounded-2xl")}
          >
            <DockIcon active={moreOpen}>
              <Ellipsis className="w-5 h-5" />
            </DockIcon>
          </button>

          <AnimatePresence>
            {moreOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className={cn(
                  "absolute bottom-full mb-3 right-0 w-52 overflow-hidden",
                  isPersonal
                    ? "bg-white dark:bg-[#0e0e10] border border-[#dadada] dark:border-gray-700 rounded-md shadow-sm"
                    : "bg-dark-card/95 backdrop-blur-xl border border-white/[0.08] rounded-2xl shadow-2xl",
                )}
              >
                {moreItems.map((item, idx) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMoreOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                      isPersonal
                        ? "text-gray-600 dark:text-gray-400 hover:text-[#0e0e10] dark:hover:text-white hover:bg-[#f1f1f2] dark:hover:bg-gray-800 font-light"
                        : "font-medium text-dark-text-secondary hover:text-white hover:bg-white/[0.06]",
                    )}
                  >
                    <item.icon size={15} strokeWidth={isPersonal ? 1 : undefined} />
                    <span>{item.label}</span>
                  </Link>
                ))}
                <div className={cn("border-t", isPersonal ? "border-[#dadada] dark:border-gray-700" : "border-white/[0.06]")} />
                <button
                  onClick={handleLogout}
                  className={cn("w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors",
                    isPersonal ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20" : "text-red-400 hover:bg-red-400/10")}
                >
                  <LogOut size={16} strokeWidth={isPersonal ? 1 : undefined} />
                  <span>Logout</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* ── Login (tamu) ── */}
      {!isAuthenticated && (
        <Link
          to="/login"
          className={cn("relative flex items-center justify-center w-11 h-11 transition-all duration-200 group", isPersonal ? "rounded-md" : "rounded-2xl")}
        >
          <DockIcon active>
            <LogIn className="w-5 h-5" />
          </DockIcon>
        </Link>
      )}
    </motion.nav>
  );
}
